import { Fragment, type FC } from 'react'
import { observer } from 'mobx-react-lite'
import { type IBoxProps } from 'shared/ui/Box'
import {
  Box,
  Button,
  Error,
  IconButton,
  type ITooltipProps,
  Label,
  PhoneWithLabelInput,
} from 'shared/ui'
import { TextField } from 'shared/ui/TextField'
import { Typography } from 'shared/ui/Typography'
import { getAriaLabel } from 'shared/lib'
import {
  type IRcsNumber,
  type IRcsWebsite,
  type IRcsEmail,
} from 'pages/settings/pages/rcsV2/pages/SenderDetails/store/type'

import styles from './styles.module.scss'

type IBrandContactSectionFieldProps = {
  values: IRcsWebsite[] | IRcsNumber[] | IRcsEmail[]
  hasValues: boolean
  canAdd: boolean
  add: () => void
  remove: (id: string) => void
  setValue?: (id: string, value: string) => void
  setLabel: (id: string, label: string) => void
  loading?: boolean
  textLabel: string
  textDescription: string
  textHeaderValue: string
  valuePlaceholder?: string
  boxProps?: IBoxProps
  canDelete?: boolean
  addButtonTooltipProps?: ITooltipProps
}

export const BrandContactSectionField: FC<IBrandContactSectionFieldProps> = observer(
  ({
    values,
    hasValues,
    canAdd,
    add,
    remove,
    loading = false,
    textLabel,
    textDescription,
    textHeaderValue,
    valuePlaceholder,
    setValue,
    setLabel,
    boxProps,
    canDelete = true,
    addButtonTooltipProps,
  }) => {
    return (
      <Fragment>
        <Box
          display={'flex'}
          gap={'24px'}
          pb={hasValues ? '40px' : '16px'}
          pt={'35px'}
          flexDirection={'column'}
          {...boxProps}
        >
          <Box
            display={'flex'}
            justifyContent={'space-between'}
            alignItems={'center'}
            width={'100%'}
          >
            <Box display={'flex'} gap={'4px'} flexDirection={'column'}>
              <Label
                label={{
                  text: textLabel,
                  textColor: 'var(--content-primary-primary)',
                  horizontal: true,
                }}
              />
              <Typography
                variant={'body-md-regular'}
                color={'var(--content-primary-secondary)'}
                ariaLabel={getAriaLabel('BrandContactSection_valueLabel')}
              >
                {textDescription}
              </Typography>
            </Box>
            <Button
              text={'Add'}
              contained={'secondary'}
              color={'primary'}
              size={'medium'}
              disabled={!canAdd}
              tooltipProps={addButtonTooltipProps}
              className={styles.addButton}
              icon={'add'}
              onClick={add}
              aria-label={getAriaLabel('BrandContactSection_addValue')}
            />
          </Box>
          <Box display={'flex'} gap={'12px'} flexDirection={'column'}>
            {hasValues && (
              <div className={styles.header}>
                <Typography
                  variant={'body-md-regular'}
                  color={'var(--content-primary-secondary)'}
                  ariaLabel={getAriaLabel('BrandContactSection_textHeader_value')}
                >
                  {textHeaderValue}
                </Typography>
                <Typography
                  width={'194px'}
                  variant={'body-md-regular'}
                  color={'var(--content-primary-secondary)'}
                  ariaLabel={getAriaLabel('BrandContactSection_textHeader_label')}
                >
                  Label
                </Typography>
              </div>
            )}

            {values.map((value) => (
              <Box key={value.id} display={'flex'} flexDirection={'column'}>
                <Box
                  display={'flex'}
                  flexDirection={'row'}
                  alignItems={'center'}
                  justifyContent={'space-between'}
                  gap={'12px'}
                >
                  <Box width={'254px'}>
                    {'phoneInputStore' in value && (
                      <PhoneWithLabelInput
                        hideLabel={true}
                        item={value.phoneInputStore.country}
                        items={value.phoneInputStore.countries}
                        loading={loading}
                        number={value.phoneInputStore.number}
                        numberError={value.phoneInputStore.numberError}
                        isValidNumber={value.phoneInputStore.isValidNumber}
                        hideError={true}
                        numberRequestError={
                          value.phoneInputStore.numberRequestError ||
                          value.phoneInputStore.numberError
                        }
                        setNumber={value.phoneInputStore.setNumber}
                        setCountry={value.phoneInputStore.setCountry}
                        triggerNumberValidation={value.phoneInputStore.triggerNumberValidation}
                      />
                    )}
                    {'url' in value && (
                      <TextField
                        size='medium'
                        variant='stroke'
                        error={value.urlError}
                        InputProps={{
                          placeholder: valuePlaceholder,
                        }}
                        value={value.url}
                        onChange={(_value) => setValue?.(value.id, _value)}
                      />
                    )}
                    {'email' in value && (
                      <TextField
                        size='medium'
                        variant='stroke'
                        error={value.emailError}
                        InputProps={{
                          placeholder: valuePlaceholder,
                        }}
                        value={value.email}
                        onChange={(_value) => setValue?.(value.id, _value)}
                      />
                    )}
                  </Box>
                  <Box display={'flex'} flexDirection={'column'} width={'150px'}>
                    <TextField
                      size='medium'
                      variant='stroke'
                      error={value.labelError}
                      InputProps={{
                        placeholder: 'Enter label',
                      }}
                      value={value.label}
                      onChange={(label: string) => setLabel(value.id, label)}
                    />
                  </Box>
                  <IconButton
                    ariaLabel={getAriaLabel('RemoveRcsValue')}
                    icon='close'
                    size='medium'
                    variant='icon'
                    color='tertiary'
                    loading={loading}
                    tooltipProps={{
                      label: 'Remove',
                      placement: 'top',
                    }}
                    disabled={!canDelete}
                    onClick={() => remove(value.id)}
                  />
                </Box>
                <Box display={'flex'} justifyContent={'space-between'} mt={'4px'}>
                  <div>
                    {'phoneInputStore' in value &&
                      (value.phoneInputStore.numberRequestError ||
                        value.phoneInputStore.numberError) && (
                        <Error
                          type='text'
                          error={
                            value.phoneInputStore.numberRequestError ||
                            value.phoneInputStore.numberError
                          }
                        />
                      )}
                    {'urlError' in value && value.urlError && (
                      <Error type='text' error={value.urlError} />
                    )}
                    {'emailError' in value && value.emailError && (
                      <Error type='text' error={value.emailError} />
                    )}
                  </div>
                  <Box width={'196px'}>
                    <Error type='text' error={value.labelError} />
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Fragment>
    )
  }
)
